import { JOB_STATUS } from '../../../constants/dentist/jobStatus';
import { Job } from '../../../types/dentist/calendar';

/**
 * T2-6191: shared "action required" predicate for the DO calendar so the same
 * red indicator (date dot + shift-card badge) is driven by one rule everywhere
 * (home calendar, JobSelectionModal, DentistJobsForDatePage, and — mirrored —
 * the legacy F7 home.js).
 *
 * A completed shift is "action required" when EITHER:
 *   1. it has an unpaid invoice (existing rule — unchanged), OR
 *   2. its DO feedback survey is still outstanding (new rule).
 *
 * Deliberately independent of the survey *popup* gating (skip count / 30-day
 * suppression from T2-5749): the badge keeps showing so the office can
 * self-serve from the Completed Shift page even after the popup stops auto-opening.
 */

type ActionRequiredJob = Omit<Partial<Job>, 'effectiveStatus' | 'status' | 'hygienistId' | 'hygienistRating' | 'feedbackSubmitted' | 'surveyDismissed' | 'invoice'> & {
  status?: number | string;
  effectiveStatus?: number | string;
  hygienistId?: number | string | null;
  hygienistRating?: number | string | null;
  feedbackSubmitted?: boolean | number | string | null;
  surveyDismissed?: boolean | number | string | null;
  invoice?: { id?: number; dentistMarkedPaid?: boolean | number | string | null };
};

const truthy = (v: unknown): boolean => v === true || v === 1 || v === '1';
const toNumber = (value: unknown): number => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const isCompleted = (job: ActionRequiredJob): boolean =>
  toNumber(job?.effectiveStatus) === JOB_STATUS.COMPLETED || toNumber(job?.status) === JOB_STATUS.COMPLETED;

/** Existing rule: completed shift with an invoice the DO has not marked paid. */
export const isUnpaidInvoiceActionRequired = (job: ActionRequiredJob): boolean => {
  const hasInvoice = !!job?.invoice && typeof job.invoice.id !== 'undefined';
  const paid = job?.invoice?.dentistMarkedPaid as unknown;
  const notPaid = paid === false || paid === 0 || paid === '0' || paid === undefined || paid === null;
  return isCompleted(job) && hasInvoice && notPaid;
};

/** New rule (T2-6191): completed shift whose DO feedback survey is outstanding. */
export const isFeedbackSurveyPending = (job: ActionRequiredJob): boolean => {
  const hasHygienist = toNumber(job?.hygienistId) > 0;
  const submitted = truthy(job?.feedbackSubmitted);
  const dismissed = truthy(job?.surveyDismissed);
  const rated = toNumber(job?.hygienistRating) > 0;
  const hasFeedback = submitted || dismissed || rated;
  return isCompleted(job) && hasHygienist && !hasFeedback;
};

/** Single source of truth for the red "action required" indicator. */
export const isJobActionRequired = (job: ActionRequiredJob): boolean =>
  isUnpaidInvoiceActionRequired(job) || isFeedbackSurveyPending(job);
